import "./styles/App.css";

import { Stack, ThemeProvider } from "@fluentui/react";
import React, { useEffect, useMemo, useRef } from "react";
import { connect, ConnectedProps } from "react-redux";

import { authProvider } from "./auth/authProvider";
import { AccessibleNavigationAnnouncer } from "./components/AccessibleLocationAnnouncer";
import GlobalErrorMessage from "./components/GlobalErrorMessage";
import Header from "./components/Header";
import ModalSpinner from "./components/ModalSpinner";
import NavigationMenu from "./components/NavigationMenu";
import Router from "./components/Router";
import ServiceStatusMessage from "./components/ServiceStatusMessage";
import { getUserRegion } from "./redux/actions/NewTopologyActions";
import { RootState } from "./redux/setupStore";
import { setUser } from "./redux/slices/appSlice";
import { darkTheme, defaultTheme } from "./styles/themes";
import { currentEnvironment } from "./utils/environment";

const App: React.FC<IReduxProps> = ({ isDarkMode, fetching, setUser, getUserRegionAction, userRegion }) => {
  const currentTheme = useMemo(() => (isDarkMode ? darkTheme : defaultTheme), [isDarkMode]);
  const hasAttemptedUserRegionFetch = useRef(false);

  // Load and set user on load, and fetch user region after authentication
  useEffect(() => {
    const cachedUser = authProvider.getAccount();

    if (cachedUser !== null) {
      const upn = cachedUser.username;
      const name = cachedUser.name !== undefined ? cachedUser.name : cachedUser.username;
      const user = {
        upn,
        name,
      };

      setUser(user);

      // After user is set and authenticated, fetch user region if not already available
      if (!userRegion.data && !userRegion.isLoading && !hasAttemptedUserRegionFetch.current) {
        hasAttemptedUserRegionFetch.current = true;
        getUserRegionAction(currentEnvironment());
      }
    }
  }, [setUser, userRegion.data, userRegion.isLoading]);

  return (
    <ThemeProvider theme={currentTheme} applyTo="body">
      <AccessibleNavigationAnnouncer />
      <Stack tokens={{ childrenGap: 15 }}>
        <Header />

        <Stack horizontal tokens={{ childrenGap: 15 }} horizontalAlign="center" wrap>
          <Stack.Item>
            <NavigationMenu />
          </Stack.Item>
          <Stack.Item styles={{ root: { flexBasis: 1000 } }}>
            <main>
              <Stack tokens={{ childrenGap: 10 }}>
                <ServiceStatusMessage />
                <GlobalErrorMessage />
                <Router />
              </Stack>
            </main>
          </Stack.Item>
        </Stack>
      </Stack>
      {fetching && <ModalSpinner />}
    </ThemeProvider>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    isDarkMode: state.appState.theme === "Dark",
    fetching: state.appState.fetching,
    userRegion: state.userRegion,
  };
};

export const mapDispatchToProps = {
  setUser,
  getUserRegionAction: getUserRegion,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type IReduxProps = ConnectedProps<typeof connector>;

export default connector(App);
